<template>
  <b-card>
    <b-tabs v-model="activeTab">
      <b-tab title="تسویه دستی" active>
        <b-alert class="mt-4" variant="danger" :show="error != null">
          {{ error }}
        </b-alert>

        <b-form class="mt-4" @submit.prevent="submitManualSettlement">
          <b-row>
            <b-col
              v-for="field in fields"
              :key="field.key"
              cols="12"
              md="4"
              sm="6"
            >
              <b-form-group :label="field.label">
                <NumberSeprator
                  v-if="field.type === 'number'"
                  v-model="form[field.key]"
                  :placeholder="field.placeholder"
                ></NumberSeprator>
                <b-form-select
                  v-else-if="field.type === 'select'"
                  v-model="form[field.key]"
                  :options="field.options"
                ></b-form-select>
                <b-form-textarea
                  v-else-if="field.type === 'textarea'"
                  v-model="form[field.key]"
                  :placeholder="field.placeholder"
                  rows="3"
                ></b-form-textarea>
                <b-form-input
                  v-else
                  v-model="form[field.key]"
                  :placeholder="field.placeholder"
                ></b-form-input>
              </b-form-group>
            </b-col>
          </b-row>

          <b-button
            type="submit"
            variant="success"
            :disabled="isSubmitting"
            :class="isSubmitting ? 'pl-7 spinner spinner-light' : ''"
          >
            ارسال درخواست
          </b-button>
        </b-form>
      </b-tab>

      <b-tab title="آپلود اکسل">
        <b-alert class="mt-4" variant="danger" :show="uploadError != null">
          {{ uploadError }}
        </b-alert>
        <b-alert class="mt-4" variant="success" :show="uploadSuccess != null">
          {{ uploadSuccess }}
        </b-alert>

        <b-form-group class="mt-4" label="فایل اکسل">
          <b-form-file
            v-model="excelFile"
            accept=".xls,.xlsx,.csv"
            browse-text="انتخاب فایل"
            placeholder="فایلی انتخاب نشده است"
            drop-placeholder="فایل را اینجا رها کنید"
          ></b-form-file>
        </b-form-group>

        <b-button
          variant="success"
          :disabled="!excelFile || isUploadingExcel"
          :class="isUploadingExcel ? 'pl-7 spinner spinner-light' : ''"
          @click="uploadExcelFile"
        >
          آپلود فایل
        </b-button>
        <b-button
          class="ml-4"
          variant="outline-info"
          @click="showExcelHelpModal = true"
        >
          راهنما
        </b-button>

        <b-table
          class="mt-4 excel-uploads-table"
          responsive
          :busy="isLoadingUploads"
          striped
          hover
          show-empty
          empty-text="آیتمی جهت نمایش وجود ندارد"
          :items="excelUploads"
          :fields="excelUploadFields"
          @row-clicked="openBatchDetails"
        >
          <template v-slot:table-busy>
            <div class="text-center my-4">
              <b-spinner class="align-middle"></b-spinner>
              <strong class="mr-2">در حال دریافت اطلاعات...</strong>
            </div>
          </template>
          <template #cell(batchId)="row">
            <copyable-table-cell :value="row.item.batchId" label="شناسه دسته" />
          </template>
          <template #cell(status)="row">
            <b-badge :variant="getUploadStatusVariant(row.item.status)" pill>
              {{ getUploadStatusLabel(row.item.status) }}
            </b-badge>
          </template>
        </b-table>

        <b-pagination
          v-if="excelUploadsTotal > excelUploadsPageSize"
          v-model="excelUploadsCurrentPage"
          class="mt-3"
          :total-rows="excelUploadsTotal"
          :per-page="excelUploadsPageSize"
          @change="fetchExcelUploads"
        ></b-pagination>
      </b-tab>
    </b-tabs>

    <b-modal
      v-model="showBatchDetailsModal"
      hide-footer
      centered
      dialog-class="batch-details-modal-dialog"
      @hidden="selectedBatchId = null"
    >
      <template #modal-title>
        <span class="batch-details-modal-title">
          جزئیات آپلود شماره
          <copyable-table-cell
            v-if="selectedBatchId"
            :value="selectedBatchId"
            label="شناسه دسته"
          />
        </span>
      </template>
      <excel-upload-batch-details
        v-if="selectedBatchId"
        :batch-id="selectedBatchId"
        :gateway-pin="gatewayPin"
        :bank-options="bankOptions"
      />
    </b-modal>

    <b-modal
      v-model="showExcelHelpModal"
      hide-footer
      title="راهنمای آپلود فایل"
      centered
    >
      <ul class="excel-help-list mb-4">
        <li>فایل Excel/CSV با حداکثر ۵ مگابایت آپلود شود.</li>
        <li>
          ستون‌های orderId، amount، destinationIban، paymentId حداقل باید وجود
          داشته و در سطر اول فایل باشند.
        </li>
        <li>هر سطر باید orderId یکتا در کل سیستم داشته باشد.</li>
        <li>مبلغ حداقل ۱۰٬۰۰۰ ریال و عدد معتبر باشد.</li>
        <li>شبا با فرمت IR + ۲۴ رقم (مثال: IR000000000000000000000000).</li>
        <li>شناسه و توضیحات اختیاری هستند.</li>
      </ul>
      <b-button variant="primary" @click="downloadExcelExampleFile">
        دانلود فایل راهنما
      </b-button>
    </b-modal>
  </b-card>
</template>

<script>
import httpHelper from "../helpers/httpHelper";
import NumberSeprator from "./numberSeprator";
import CopyableTableCell from "./CopyableTableCell";
import ExcelUploadBatchDetails from "./ExcelUploadBatchDetails.vue";
import moment from "jalali-moment";
import { EMPTY_VALUE } from "../helpers/copyableHelper";
import { buildReasonDescriptionOptions } from "../helpers/reasonDescriptionHelper";

const EXCEL_UPLOAD_TAB_INDEX = 1;

const UPLOAD_STATUS_MAP = {
  UPLOADED: { label: "آپلود شده", variant: "info" },
  PARSING_FAILED: { label: "خطا در پردازش", variant: "danger" },
  COMPLETED: { label: "پردازش شده", variant: "success" },
};

export default {
  name: "ManualSettlement",
  components: {
    NumberSeprator,
    CopyableTableCell,
    ExcelUploadBatchDetails,
  },
  props: {
    gatewayPin: {
      type: [String, Number],
      default: null,
    },
    bankOptions: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      activeTab: 0,
      isSubmitting: false,
      isUploadingExcel: false,
      error: null,
      uploadError: null,
      uploadSuccess: null,
      excelFile: null,
      isLoadingUploads: false,
      excelUploads: [],
      excelUploadsCurrentPage: 1,
      excelUploadsPageSize: 50,
      excelUploadsTotal: 0,
      showBatchDetailsModal: false,
      showExcelHelpModal: false,
      selectedBatchId: null,
      excelUploadFields: [
        {
          key: "batchId",
          label: "شناسه دسته",
        },
        {
          key: "status",
          label: "وضعیت",
        },
        {
          key: "processedRows",
          label: "ردیف‌های پردازش‌شده",
          formatter: (value) => value || EMPTY_VALUE,
        },
        {
          key: "failedRows",
          label: "ردیف‌های ناموفق",
          formatter: (value) => value || EMPTY_VALUE,
        },
        {
          key: "totalRows",
          label: "کل ردیف‌ها",
          formatter: (value) => value || EMPTY_VALUE,
        },
        {
          key: "createdAt",
          label: "تاریخ ایجاد",
          formatter: (value) => this.formatDateTime(value),
        },
        {
          key: "updatedAt",
          label: "تاریخ بروزرسانی",
          formatter: (value) => this.formatDateTime(value),
        },
      ],
      reasonDescriptionOptions: buildReasonDescriptionOptions(),
      form: {},
    };
  },
  computed: {
    fields() {
      return [
        {
          key: "orderId",
          label: "شناسه سفارش",
          placeholder: "ORD-2024-001",
        },
        {
          key: "amount",
          label: "مبلغ (ریال)",
          type: "number",
          placeholder: "مبلغ را وارد کنید",
        },
        {
          key: "destinationIban",
          label: "شماره شبا مقصد",
          placeholder: "IR120170000000123456789001",
        },
        {
          key: "destinationFirstName",
          label: "نام گیرنده",
          placeholder: "علی",
        },
        {
          key: "destinationLastName",
          label: "نام خانوادگی گیرنده",
          placeholder: "احمدی",
        },
        {
          key: "destinationBank",
          label: "بانک مقصد",
          type: "select",
          options: this.bankOptions,
        },
        {
          key: "reasonDescription",
          label: "بابت",
          type: "select",
          options: this.reasonDescriptionOptions,
        },
        {
          key: "paymentId",
          label: "شناسه واریز (اختیاری)",
          placeholder: "PAY-123",
        },
        {
          key: "description",
          label: "توضیحات (اختیاری)",
          type: "textarea",
          placeholder: "متن توضیحات",
        },
      ];
    },
  },
  watch: {
    activeTab(newTab) {
      if (newTab === EXCEL_UPLOAD_TAB_INDEX) {
        this.fetchExcelUploads(1);
      }
    },
  },
  methods: {
    createEmptyForm() {
      return {
        orderId: null,
        amount: null,
        destinationIban: null,
        destinationFirstName: null,
        destinationLastName: null,
        destinationBank: null,
        reasonDescription: null,
        paymentId: null,
        description: null,
      };
    },
    normalizeAmount(value) {
      const normalized = String(value == null ? "" : value).replace(/,/g, "");
      const amount = Number(normalized);
      return Number.isInteger(amount) ? amount : null;
    },
    isEmptyFormValue(value) {
      return value == null || String(value).trim() === "";
    },
    isValidIban(value) {
      return /^(I|i)(R|r)[0-9]{24}$/.test(String(value).trim());
    },
    formatDateTime(value) {
      if (!value) {
        return EMPTY_VALUE;
      }

      const date = moment(value);
      return date.isValid() ? date.format("jYYYY/jMM/jDD | HH:mm:ss") : value;
    },
    getUploadStatusLabel(status) {
      return (
        (UPLOAD_STATUS_MAP[status] && UPLOAD_STATUS_MAP[status].label) ||
        status ||
        EMPTY_VALUE
      );
    },
    getUploadStatusVariant(status) {
      return (
        (UPLOAD_STATUS_MAP[status] && UPLOAD_STATUS_MAP[status].variant) ||
        "secondary"
      );
    },
    downloadExcelExampleFile() {
      const link = document.createElement("a");
      link.href = "/transfer-by-excel-example.csv";
      link.setAttribute("download", "transfer-by-excel-example.csv");
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    },
    openBatchDetails(item) {
      if (!item || !item.batchId) {
        return;
      }
      this.selectedBatchId = item.batchId;
      this.showBatchDetailsModal = true;
    },
    fetchExcelUploads(page = 1) {
      if (!this.gatewayPin) {
        this.excelUploads = [];
        this.excelUploadsTotal = 0;
        return;
      }

      httpHelper.setAuth();
      this.isLoadingUploads = true;
      this.excelUploadsCurrentPage = page;

      httpHelper
        .getData(
          `~transfer/transfer/uploads?api=${this.gatewayPin}&page=${page}&perPage=${this.excelUploadsPageSize}`,
        )
        .then((res) => {
          this.excelUploads = res && res.data ? res.data : [];
          const meta = res && res.meta ? res.meta : {};
          this.excelUploadsTotal = meta.total || 0;
          this.excelUploadsCurrentPage = meta.page || page;
          this.excelUploadsPageSize = meta.perPage || this.excelUploadsPageSize;
        })
        .catch(() => {
          this.excelUploads = [];
          this.excelUploadsTotal = 0;
        })
        .then(() => {
          this.isLoadingUploads = false;
        });
    },
    uploadExcelFile() {
      this.uploadError = null;
      this.uploadSuccess = null;

      if (!this.excelFile) {
        this.uploadError = "ابتدا فایل اکسل را انتخاب کنید.";
        return;
      }

      const extension = this.excelFile.name.split(".").pop().toLowerCase();
      if (!["xls", "xlsx", "csv"].includes(extension)) {
        this.uploadError = "فقط فایل‌های xls، xlsx و csv مجاز هستند.";
        return;
      }

      if (this.excelFile.size > 5 * 1024 * 1024) {
        this.uploadError = "حجم فایل نباید بیشتر از ۵ مگابایت باشد.";
        return;
      }

      httpHelper.setAuth();
      this.isUploadingExcel = true;
      const formData = new FormData();
      formData.append("file", this.excelFile);

      httpHelper
        .postData(`~transfer/transfer/upload?api=${this.gatewayPin}`, formData)
        .then((res) => {
          const data = res && res.data ? res.data : {};
          if (data.success === false) {
            this.uploadError = data.message || "خطا در آپلود فایل.";
            return;
          }
          this.uploadSuccess = data.message || "فایل با موفقیت آپلود شد.";
          this.$makeToast("success", "آپلود موفق", this.uploadSuccess, 5000);
          if (this.activeTab === EXCEL_UPLOAD_TAB_INDEX) {
            this.fetchExcelUploads(1);
          }
        })
        .catch((error) => {
          this.uploadError =
            (error.response &&
              error.response.data &&
              (error.response.data.message || error.response.data.error)) ||
            "خطا در آپلود فایل.";
        })
        .then(() => {
          this.isUploadingExcel = false;
          this.excelFile = null;
        });
    },
    submitManualSettlement() {
      this.error = null;
      const missingField = this.fields.find((field) => {
        if (["description", "paymentId"].includes(field.key)) return false;
        return this.isEmptyFormValue(this.form[field.key]);
      });

      if (missingField) {
        this.error = `${missingField.label} را وارد کنید.`;
        return;
      }

      if (!this.isValidIban(this.form.destinationIban)) {
        this.error = "فرمت شماره شبا مقصد صحیح نمی باشد.";
        return;
      }

      const amount = this.normalizeAmount(this.form.amount);

      if (amount == null) {
        this.error = "مبلغ باید عدد صحیح باشد.";
        return;
      }
      if (!this.gatewayPin) {
        this.error = "اطلاعات درگاه دریافت نشده است.";
        return;
      }

      httpHelper.setAuth();
      this.isSubmitting = true;
      httpHelper
        .postData("~transfer/transfer", {
          ...this.form,
          api: this.gatewayPin,
          amount,
        })
        .then((res) => {
          const data = res && res.data ? res.data : {};
          if (data.success === false) {
            this.error = data.message || "خطا در ارسال اطلاعات.";
            return;
          }
          this.$makeToast(
            "success",
            "ارسال موفق",
            "درخواست تسویه دستی با موفقیت ارسال شد.",
            5000,
          );
          this.form = this.createEmptyForm();
        })
        .catch((error) => {
          this.error =
            (error.response &&
              error.response.data &&
              (error.response.data.message || error.response.data.error)) ||
            "خطا در ارسال اطلاعات.";
        })
        .then(() => {
          this.isSubmitting = false;
        });
    },
  },
  mounted() {
    this.form = this.createEmptyForm();
  },
};
</script>

<style scoped>
.excel-uploads-table >>> tbody tr {
  cursor: pointer;
}

.excel-help-list {
  padding-right: 1.25rem;
  line-height: 1.8;
}
</style>

<style>
.batch-details-modal-dialog {
  max-width: min(1600px, 96vw);
  width: 96vw;
}

.batch-details-modal-title {
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
}
</style>
